/**
 * Instant Search Widget — powered by Boost Commerce search API.
 *
 * Attaches to header search inputs and renders a live results dropdown.
 * Requires `window.__instantSearchShop` to be set to the Shopify permanent domain.
 *
 * Features:
 *   - Two-endpoint parallel fetch: /search (products + filter facets) and /search/suggest (collections, pages)
 *   - Filter pills from facets (Product Type, Brand) for quick refinement
 *   - Sidebar with matched collections, pages, and autocomplete suggestions
 *   - Fabric variant expansion (multi-variant products shown per-variant)
 *   - Search-term redirects: a matching query navigates away instead of listing results
 *   - Keyboard navigation, caching, debounce, loading states
 */
(function () {
  'use strict';

  const API = 'https://services.mybcapps.com/bc-sf-filter/search';
  const API_SUGGEST = 'https://services.mybcapps.com/bc-sf-filter/search/suggest';
  const DEBOUNCE = 300;
  const RESULT_LIMIT = 8;
  // Cards, not products: variant expansion can turn 8 fabrics into 28 rows,
  // which is what used to push the dropdown into a scroll.
  const CARD_LIMIT = 8;
  // Colour swatches shown per card before collapsing into "+ N more colours".
  const SWATCH_LIMIT = 4;
  const CURRENCY = '£';

  // ── Helpers ──────────────────────────────────────

  function formatPrice(price) {
    if (price == null || isNaN(price)) return '';
    return CURRENCY + Number(price).toFixed(2);
  }

  var SKIP_EXPAND_TYPES = ['Sewing Machines', 'Gift Card'];

  // The support (Gorgias chat) button only shows when the results include a
  // machine, where human help is worth surfacing. In this store's taxonomy
  // every machine — sewing, embroidery, overlocker, coverstitch — carries the
  // single product_type 'Sewing Machines'; there are no separate machine types.
  // Kept as a list so it still works if that ever splits.
  var MACHINE_TYPES = ['Sewing Machines'];

  /** True if any result product is a machine (see MACHINE_TYPES). */
  function hasMachineResult(products) {
    for (var i = 0; i < products.length; i++) {
      if (MACHINE_TYPES.indexOf(products[i].product_type) !== -1) return true;
    }
    return false;
  }

  function getImage(product) {
    if (product.images_info && product.images_info[0]) return product.images_info[0].src;
    if (product.image && product.image.src) return product.image.src;
    if (product.images && product.images[0]) return product.images[0];
    return '';
  }

  function getVariantImage(product, variantId) {
    var vid = String(variantId);
    var variant = null;
    if (product.variants) {
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === vid) { variant = product.variants[i]; break; }
      }
    }
    if (variant && variant.image && typeof variant.image === 'string') return variant.image;
    if (product.images_info) {
      for (var j = 0; j < product.images_info.length; j++) {
        var img = product.images_info[j];
        if (Array.isArray(img.variant_ids)) {
          for (var k = 0; k < img.variant_ids.length; k++) {
            if (String(img.variant_ids[k]) === vid) return img.src;
          }
        }
      }
      if (variant && variant.image_id) {
        for (var m = 0; m < product.images_info.length; m++) {
          if (String(product.images_info[m].id) === String(variant.image_id)) return product.images_info[m].src;
        }
      }
      if (variant && variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    }
    return getImage(product);
  }

  /** Should this product be expanded into individual variant rows? */
  function shouldExpand(product) {
    if (!product.variants || product.variants.length <= 1) return false;
    if (SKIP_EXPAND_TYPES.indexOf(product.product_type) !== -1) return false;
    return true;
  }

  // Only fabrics and threads carry their colours/designs as variants worth
  // previewing as swatches; machines, accessories etc. are excluded even if
  // they happen to have a colour option.
  function isSwatchType(product) {
    var t = product.product_type || '';
    return t === 'Threads' || t.indexOf('Fabric') === 0;
  }

  /**
   * Swatch info for a fabric/thread product. Threads carry each colour as a
   * variant and fabrics each design, so a single result card would otherwise
   * hide that there are dozens behind it. The swatch option's values each have
   * an `image` index into images_info (by position), which we resolve to a real
   * thumbnail. Returns { count, swatches:[{src,title}], noun } or null when the
   * product isn't a fabric/thread, has no swatch option, or has only one value.
   */
  function swatchInfo(product) {
    if (!isSwatchType(product)) return null;
    var opts = product.options_with_values;
    if (!Array.isArray(opts)) return null;

    // Threads swatch on colour, fabrics on design; prefer colour when both.
    var opt = null, noun = 'colour';
    var wanted = ['colour', 'color', 'design'];
    for (var w = 0; w < wanted.length && !opt; w++) {
      for (var i = 0; i < opts.length; i++) {
        var name = (opts[i].original_name || opts[i].name || opts[i].label || '').toLowerCase();
        if (name === wanted[w]) { opt = opts[i]; noun = (wanted[w] === 'design') ? 'design' : 'colour'; break; }
      }
    }
    if (!opt || !Array.isArray(opt.values) || opt.values.length <= 1) return null;

    var byPos = {};
    var imgs = product.images_info || [];
    for (var j = 0; j < imgs.length; j++) {
      if (imgs[j] && imgs[j].position != null) byPos[imgs[j].position] = imgs[j].src;
    }
    var swatches = [];
    for (var k = 0; k < opt.values.length; k++) {
      var val = opt.values[k] || {};
      swatches.push({ src: byPos[val.image] || '', title: val.title || '' });
    }
    return { count: opt.values.length, swatches: swatches, noun: noun };
  }

  /**
   * Check if a variant title matches any of the search terms.
   */
  function variantMatchesQuery(variant, queryWords) {
    var title = (variant.title || '').toLowerCase();
    for (var i = 0; i < queryWords.length; i++) {
      if (title.indexOf(queryWords[i]) !== -1) return true;
    }
    return false;
  }

  /**
   * Expand products into a flat cards array.
   * For expandable products (multi-variant fabrics etc.), check if any variant
   * title matches the search query. If so, show only matching variants.
   * If no variant matches, show the product as a single row.
   */
  function expandToCards(products, query) {
    var queryWords = (query || '').toLowerCase().split(/\s+/).filter(function (w) { return w.length > 0; });
    var cards = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (shouldExpand(p) && queryWords.length > 0) {
        var tags = (p.tags || []).map(function (t) { return typeof t === 'string' ? t.toLowerCase() : ''; });
        var isComingSoon = tags.indexOf('comingsoon') !== -1;
        var isRepeatable = tags.indexOf('repeatable') !== -1;
        var matched = [];
        for (var v = 0; v < p.variants.length; v++) {
          var variant = p.variants[v];
          if (!variant.available && !isComingSoon && !isRepeatable) continue;
          if (variantMatchesQuery(variant, queryWords)) {
            matched.push(variant);
          }
        }
        if (matched.length > 0) {
          for (var m = 0; m < matched.length; m++) {
            cards.push({ type: 'variant', product: p, variant: matched[m] });
          }
        } else {
          cards.push({ type: 'product', product: p });
        }
      } else {
        cards.push({ type: 'product', product: p });
      }
    }
    return cards;
  }

  function thumbUrl(src, width) {
    if (!src) return '';
    var m = src.match(/^(.*?)(\.\w+)(\?.*)?$/);
    if (!m) return src;
    return m[1] + '_' + width + 'x' + m[2] + (m[3] || '');
  }

  function escHtml(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  /**
   * Sidebar row. Long titles are clipped to one line by CSS, so carry the full
   * text in the title attribute.
   */
  function sidebarLink(label, href) {
    return '<li class="isearch-sidebar__item">' +
      '<a class="isearch-sidebar__link" data-isearch-item title="' + escHtml(label) + '" href="' + escHtml(href) + '">' +
      escHtml(label) + '</a></li>';
  }

  /**
   * "Chat with us" support button for the dropdown footer. Replicates Boost's
   * Gorgias integration: the open dropdown covers the floating chat bubble, so
   * we surface an entry point to Gorgias inside the widget itself. Desktop-only
   * (hidden by CSS on mobile, where the bubble stays reachable). Returns '' when
   * disabled from the theme settings or when Gorgias isn't on the page, so the
   * button never shows with nothing behind it. Also returns '' unless the
   * results include a machine (hasMachine) — chat is surfaced for machine
   * shoppers, not fabric/accessory searches. No data-isearch-item: it is an
   * action, not a result, so it stays out of arrow-key navigation.
   */
  function supportButtonHtml(hasMachine) {
    if (!hasMachine) return '';
    var cfg = window.__instantSearchSupport;
    if (cfg && cfg.enabled === false) return '';
    // Only show when the Gorgias chat has actually mounted on THIS page.
    // The chat isn't loaded on every template (e.g. the homepage), and there
    // its loader can still define window.GorgiasChat without the widget ever
    // mounting — so a `typeof GorgiasChat` check would wrongly show a button
    // that can't open anything. #gorgias-chat-container is injected only when
    // the widget is live, making it the reliable signal. The click handler
    // still waits for init to finish before calling open() (see openGorgiasChat).
    if (!document.getElementById('gorgias-chat-container')) return '';
    var label = (cfg && cfg.label) || 'Chat with our team';
    return '<button type="button" class="isearch-support-btn" data-isearch-support>' +
      '<svg class="isearch-support-btn__icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      '<span>' + escHtml(label) + '</span></button>';
  }

  /**
   * Open the Gorgias chat, waiting for the widget to finish initialising.
   *
   * Gorgias loads asynchronously, so GorgiasChat.open() called before the
   * widget is ready silently no-ops — which is why the button looked dead.
   * Per Gorgias' docs, resolve a single readiness promise (init() if the
   * object is already there, otherwise the `gorgias-widget-loaded` event),
   * then open once it settles. Built lazily so we read window.GorgiasChat at
   * click time, avoiding a race with an event that may already have fired.
   */
  var gorgiasReadyPromise = null;
  function openGorgiasChat() {
    if (!gorgiasReadyPromise) {
      if (window.GorgiasChat && typeof window.GorgiasChat.init === 'function') {
        gorgiasReadyPromise = window.GorgiasChat.init();
      } else {
        gorgiasReadyPromise = new Promise(function (resolve) {
          window.addEventListener('gorgias-widget-loaded', function onLoaded() {
            window.removeEventListener('gorgias-widget-loaded', onLoaded);
            resolve();
          });
        });
      }
    }
    gorgiasReadyPromise.then(function () {
      if (window.GorgiasChat && typeof window.GorgiasChat.open === 'function') {
        window.GorgiasChat.open();
      }
    });
  }

  // ── Search redirects ─────────────────────────────

  /**
   * Rules come from the `search_redirect` metaobject, rendered into the page by
   * snippets/search-redirect-rules.liquid. Terms arrive stripped and downcased.
   * The search results page enforces the same rules in Liquid, so a shopper who
   * bypasses the dropdown still lands in the right place; matching here just
   * saves them the interim page.
   */
  function matchRedirect(query) {
    var rules = window.__searchRedirects;
    if (!Array.isArray(rules)) return null;

    var q = (query || '').trim().toLowerCase();
    if (!q) return null;

    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i] || {};
      if (!rule.target) continue;
      var terms = rule.terms || [];
      for (var j = 0; j < terms.length; j++) {
        var term = String(terms[j] || '').trim().toLowerCase();
        if (!term) continue;
        var hit = rule.match === 'contains' ? q.indexOf(term) !== -1 : q === term;
        if (hit) return rule;
      }
    }
    return null;
  }

  // ── Cache ────────────────────────────────────────

  var cache = {};

  // ── Widget class ─────────────────────────────────

  function InstantSearch(input) {
    this.input = input;
    this.form = input.closest('form');
    this.shopDomain = window.__instantSearchShop || '';
    this.isOpen = false;
    this.query = '';
    this.timer = null;
    this.controller = null;
    this.activeIndex = -1;
    this.items = [];

    this._buildDOM();
    this._bind();
  }

  InstantSearch.prototype._buildDOM = function () {
    // Wrapper — make the form position:relative so dropdown anchors to it
    this.form.style.position = 'relative';

    // Desktop overlay (appended to body)
    this.overlay = document.createElement('div');
    this.overlay.className = 'isearch-overlay';
    document.body.appendChild(this.overlay);

    // Mobile overlay — appended inside the header so it covers header elements below the input
    this.mobileOverlay = document.createElement('div');
    this.mobileOverlay.className = 'isearch-mobile-overlay';
    var header = document.querySelector('site-header');
    if (header) {
      header.appendChild(this.mobileOverlay);
    } else {
      document.body.appendChild(this.mobileOverlay);
    }

    // Dropdown
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'isearch-dropdown';
    this.dropdown.setAttribute('role', 'listbox');
    this.dropdown.id = 'isearch-results-' + Math.random().toString(36).slice(2, 8);
    this.form.appendChild(this.dropdown);

    this.input.setAttribute('aria-controls', this.dropdown.id);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('autocomplete', 'off');
  };

  InstantSearch.prototype._bind = function () {
    var self = this;

    // Support button: open Gorgias and close the dropdown (which releases the
    // body scroll lock _open sets, so the chat panel is usable). Delegated so it
    // works for whichever footer the current render produced.
    this.dropdown.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-isearch-support]') : null;
      if (!btn) return;
      e.preventDefault();
      self._close();
      openGorgiasChat();
    });

    this.input.addEventListener('input', function () {
      clearTimeout(self.timer);
      var q = self.input.value.trim();
      if (q.length < 2) {
        self._close();
        return;
      }
      self.timer = setTimeout(function () { self._search(q); }, DEBOUNCE);
    });

    this.input.addEventListener('focus', function () {
      var q = self.input.value.trim();
      if (q.length >= 2 && self.query === q && self.dropdown.innerHTML) {
        self._open();
      }
    });

    // Close on click outside — use document listener instead of overlay
    // so the overlay never blocks clicks on the dropdown
    document.addEventListener('click', function (e) {
      if (!self.isOpen) return;
      if (self.dropdown.contains(e.target) || self.input.contains(e.target) || self.form.contains(e.target)) return;
      self._close();
    });

    this.input.addEventListener('keydown', function (e) {
      if (!self.isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        self._close();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        self._navigate(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        self._navigate(-1);
      } else if (e.key === 'Enter' && self.activeIndex >= 0) {
        e.preventDefault();
        var active = self.items[self.activeIndex];
        if (active) {
          window.location.href = active.getAttribute('href') || active.dataset.href;
        }
      }
    });

    // Prevent form submit when navigating results
    this.form.addEventListener('submit', function (e) {
      if (self.activeIndex >= 0) {
        e.preventDefault();
        var active = self.items[self.activeIndex];
        if (active) window.location.href = active.getAttribute('href') || active.dataset.href;
        return;
      }

      // Nothing picked from the dropdown, so a redirect term goes straight to
      // its destination rather than via the results page, which would only
      // bounce them there anyway.
      var rule = matchRedirect(self.input.value);
      if (rule) {
        e.preventDefault();
        window.location.href = rule.target;
      }
    });
  };

  // ── API ──────────────────────────────────────────

  InstantSearch.prototype._search = function (q) {
    var self = this;

    // Redirect terms are NOT auto-redirected while typing -- a shopper still
    // sees the live dropdown for the term. The redirect only fires when they
    // press Enter (handled in the form submit listener above), so the jump is
    // deliberate rather than yanking them off mid-keystroke.

    this.query = q;
    var key = q.toLowerCase().replace(/\s+/g, '-');

    if (cache[key]) {
      self._render(cache[key], q);
      return;
    }

    // Show loading
    this.dropdown.innerHTML = '<div class="isearch-loading"><div class="isearch-spinner"></div></div>';
    this._open();

    // Abort previous requests
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    var signal = this.controller.signal;

    // ── Main search (products + filter facets) ──
    var mainParams = new URLSearchParams();
    mainParams.set('shop', this.shopDomain);
    mainParams.set('q', q);
    mainParams.set('page', '0');
    mainParams.set('limit', String(RESULT_LIMIT));
    mainParams.set('sort', 'relevance');
    mainParams.set('build_filter_tree', 'true');
    // Let Boost drop sold-out products server-side. Filtering them out here
    // instead left the grid empty when the whole first page was sold out, and
    // left total_product overstating what a customer can actually buy.
    mainParams.set('product_available', 'true');

    // ── Suggest (collections, pages, text suggestions) ──
    var suggestParams = new URLSearchParams();
    suggestParams.set('shop', this.shopDomain);
    suggestParams.set('q', q);
    suggestParams.set('suggestionLimit', '5');
    suggestParams.set('productLimit', '0');
    suggestParams.set('collectionLimit', '5');
    suggestParams.set('pageLimit', '3');

    var mainPromise = fetch(API + '?' + mainParams.toString(), { signal: signal })
      .then(function (r) { return r.json(); });

    var suggestPromise = fetch(API_SUGGEST + '?' + suggestParams.toString(), { signal: signal })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; }); // Don't block on suggest failures

    Promise.all([mainPromise, suggestPromise])
      .then(function (responses) {
        var data = responses[0];
        var suggest = responses[1];

        var result = {
          products: data.products || [],
          total: data.total_product || 0,
          collections: (suggest && suggest.collections) || [],
          pages: (suggest && suggest.pages) || [],
          suggestions: (suggest && suggest.suggestions) || [],
          filters: (data.filter && data.filter.options) || []
        };

        // Fallback: infer collections from products if suggest returned none
        if (!result.collections.length && result.products.length) {
          var seen = {};
          for (var i = 0; i < result.products.length; i++) {
            var p = result.products[i];
            if (p.collections_info) {
              for (var j = 0; j < p.collections_info.length; j++) {
                var c = p.collections_info[j];
                if (c.handle && c.title && !seen[c.handle] && c.title.indexOf('Hidden') === -1) {
                  seen[c.handle] = true;
                  result.collections.push({ handle: c.handle, title: c.title, url: '/collections/' + c.handle });
                }
              }
            }
          }
          result.collections = result.collections.slice(0, 5);
        }

        cache[key] = result;
        if (self.query.toLowerCase().replace(/\s+/g, '-') === key) {
          self._render(result, q);
        }
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        self.dropdown.innerHTML = '<div class="isearch-empty">Something went wrong. Please try again.</div>';
      });
  };

  // ── Render ───────────────────────────────────────

  InstantSearch.prototype._render = function (result, q) {
    var products = result.products;
    var total = result.total;
    var collections = result.collections || [];
    var pages = result.pages || [];
    var suggestions = result.suggestions || [];
    var filters = result.filters || [];

    if (!products.length && !collections.length && !pages.length && !suggestions.length) {
      var emptySupport = supportButtonHtml(hasMachineResult(products));
      this.dropdown.innerHTML = '<div class="isearch-empty">No results for \u201c' + escHtml(q) + '\u201d</div>' +
        (emptySupport ? '<div class="isearch-footer isearch-footer--support">' + emptySupport + '</div>' : '');
      this.items = [];
      this.activeIndex = -1;
      this._open();
      return;
    }

    // ── Extract filter pills from facets ──
    var pills = [];
    var PILL_FILTERS = ['pf_t_product_type', 'pf_v_vendor'];
    for (var fi = 0; fi < filters.length; fi++) {
      var filter = filters[fi];
      if (PILL_FILTERS.indexOf(filter.filterOptionId) === -1) continue;
      var values = filter.values || [];
      for (var vi = 0; vi < values.length && vi < 4; vi++) {
        var val = values[vi];
        if (val.key && val.doc_count > 0) {
          pills.push({
            label: val.key,
            count: val.doc_count,
            url: '/search?q=' + encodeURIComponent(q) + '&' + filter.filterOptionId + '=' + encodeURIComponent(val.key)
          });
        }
      }
    }
    pills = pills.slice(0, 6);

    // ── Sidebar content ──
    // Normalise collection objects (suggest may return different shapes)
    var sidebarCollections = [];
    for (var ci = 0; ci < collections.length && ci < 5; ci++) {
      var col = collections[ci];
      var colTitle = col.title || '';
      var colUrl = col.url || ('/collections/' + (col.handle || ''));
      var colHandle = col.handle || '';
      if (colTitle && colTitle.indexOf('Hidden') === -1 && colHandle.indexOf('hidden') === -1) {
        sidebarCollections.push({ title: colTitle, url: colUrl });
      }
    }

    // Normalise page objects
    var sidebarPages = [];
    for (var pgi = 0; pgi < pages.length && pgi < 3; pgi++) {
      var pg = pages[pgi];
      var pgTitle = pg.title || '';
      var pgUrl = pg.url || ('/pages/' + (pg.handle || ''));
      if (pgTitle) {
        sidebarPages.push({ title: pgTitle, url: pgUrl });
      }
    }

    // Normalise suggestions
    var sidebarSuggestions = [];
    for (var si = 0; si < suggestions.length && si < 5; si++) {
      var sug = suggestions[si];
      var sugText = typeof sug === 'string' ? sug : (sug.text || sug.title || sug.query || '');
      if (sugText) sidebarSuggestions.push(sugText);
    }

    var hasSidebar = sidebarCollections.length > 0 || sidebarPages.length > 0 || sidebarSuggestions.length > 0;

    // ── Build HTML ──
    var html = '<div class="isearch-body">';

    // Sidebar
    if (hasSidebar) {
      html += '<div class="isearch-sidebar">';

      // Suggestions
      if (sidebarSuggestions.length > 0) {
        html += '<div class="isearch-sidebar__section">';
        html += '<p class="isearch-sidebar__heading">Suggestions</p>';
        html += '<ul class="isearch-sidebar__list">';
        for (var ss = 0; ss < sidebarSuggestions.length; ss++) {
          html += sidebarLink(sidebarSuggestions[ss], '/search?q=' + encodeURIComponent(sidebarSuggestions[ss]));
        }
        html += '</ul></div>';
      }

      // Collections
      if (sidebarCollections.length > 0) {
        html += '<div class="isearch-sidebar__section">';
        html += '<p class="isearch-sidebar__heading">Collections</p>';
        html += '<ul class="isearch-sidebar__list">';
        for (var sc = 0; sc < sidebarCollections.length; sc++) {
          html += sidebarLink(sidebarCollections[sc].title, sidebarCollections[sc].url);
        }
        html += '</ul></div>';
      }

      // Pages
      if (sidebarPages.length > 0) {
        html += '<div class="isearch-sidebar__section">';
        html += '<p class="isearch-sidebar__heading">Pages</p>';
        html += '<ul class="isearch-sidebar__list">';
        for (var sp = 0; sp < sidebarPages.length; sp++) {
          html += sidebarLink(sidebarPages[sp].title, sidebarPages[sp].url);
        }
        html += '</ul></div>';
      }

      html += '</div>';
    }

    // Product cards (with variant expansion), capped so the grid stays two rows
    var cards = expandToCards(products, q).slice(0, CARD_LIMIT);

    // Products column
    html += '<div class="isearch-products">';
    html += '<p class="isearch-products__heading">Products' + (cards.length && total > cards.length ? ' (' + total + ')' : '') + '</p>';

    // Filter pills
    if (pills.length > 0) {
      html += '<div class="isearch-filters">';
      for (var fp = 0; fp < pills.length; fp++) {
        html += '<a class="isearch-filter-pill" data-isearch-item href="' + escHtml(pills[fp].url) + '">';
        html += escHtml(pills[fp].label);
        html += '<span class="isearch-filter-pill__count">' + pills[fp].count + '</span>';
        html += '</a>';
      }
      html += '</div>';
    }

    // Everything matching is sold out: say so rather than leave the column blank
    if (!cards.length) {
      html += '<p class="isearch-products__none">Nothing in stock for “' + escHtml(q) + '”. Try the collections and pages listed, or view all results below.</p>';
    }

    html += '<div class="isearch-products__grid">';
    for (var pi = 0; pi < cards.length; pi++) {
      var card = cards[pi];
      var prod = card.product;
      var imgSrc, title, price, compareAt, cardUrl;

      if (card.type === 'variant') {
        var v = card.variant;
        imgSrc = thumbUrl(getVariantImage(prod, v.id), 240);
        title = prod.title + ' \u2013 ' + v.title;
        price = v.price;
        compareAt = v.compare_at_price;
        cardUrl = '/products/' + prod.handle + '?variant=' + v.id;
      } else {
        imgSrc = thumbUrl(getImage(prod), 240);
        title = prod.title;
        price = prod.variants && prod.variants[0] ? prod.variants[0].price : prod.price_min;
        compareAt = prod.variants && prod.variants[0] ? prod.variants[0].compare_at_price : prod.compare_at_price_min;
        cardUrl = '/products/' + prod.handle;
      }

      var hasCompare = compareAt != null && Number(compareAt) > Number(price);

      html += '<a class="isearch-product" data-isearch-item data-product-id="' + prod.id + '" href="' + cardUrl + '">';
      html += '<div class="isearch-product__inner">';
      html += '<div class="isearch-product__image">';
      if (imgSrc) {
        html += '<img src="' + escHtml(imgSrc) + '" alt="' + escHtml(title) + '" loading="lazy">';
      }
      html += '</div>';
      html += '<div class="isearch-product__info">';
      html += '<p class="isearch-product__title">' + escHtml(title) + '</p>';
      html += '</div>';
      html += '<div class="isearch-product__price">';
      if (hasCompare) {
        html += '<span class="isearch-product__price--was">' + formatPrice(compareAt) + '</span>';
      }
      if (card.type === 'product' && prod.price_varies) {
        html += 'From ' + formatPrice(prod.price_min);
      } else {
        html += formatPrice(price);
      }
      html += '</div>';
      var swatchData = card.type === 'product' ? swatchInfo(prod) : null;
      if (swatchData) {
        var shown = Math.min(SWATCH_LIMIT, swatchData.swatches.length);
        html += '<div class="isearch-product__swatches">';
        for (var ci = 0; ci < shown; ci++) {
          var sw = swatchData.swatches[ci];
          html += '<span class="isearch-swatch" title="' + escHtml(sw.title) + '">';
          if (sw.src) {
            html += '<img src="' + escHtml(thumbUrl(sw.src, 80)) + '" alt="" loading="lazy">';
          }
          html += '</span>';
        }
        html += '</div>';
        if (swatchData.count > shown) {
          var more = swatchData.count - shown;
          html += '<p class="isearch-product__more-colours">+ ' + more + ' more ' + swatchData.noun + (more === 1 ? '' : 's') + '</p>';
        }
      }
      html += '</div>';
      html += '</a>';
    }
    html += '</div>';

    html += '</div></div>';

    // Footer
    var support = supportButtonHtml(hasMachineResult(products));
    html += '<div class="isearch-footer' + (support ? ' isearch-footer--split' : '') + '">';
    html += support;
    html += '<a class="isearch-footer__btn" data-isearch-item href="/search?q=' + encodeURIComponent(q) + '">View all results for \u201c' + escHtml(q) + '\u201d</a>';
    html += '</div>';

    this.dropdown.innerHTML = html;
    this.items = Array.from(this.dropdown.querySelectorAll('[data-isearch-item]'));
    this.activeIndex = -1;
    this._open();
    this._injectBundleBadges();
  };

  // ── Bundle Badges ──────────────────────────────────

  InstantSearch.prototype._injectBundleBadges = function () {
    if (!window.BoostBundles) return;
    var productEls = this.dropdown.querySelectorAll('.isearch-product[data-product-id]');
    var idMap = {};
    var ids = [];
    for (var i = 0; i < productEls.length; i++) {
      var pid = productEls[i].getAttribute('data-product-id');
      if (pid && !idMap[pid]) {
        idMap[pid] = [];
        ids.push(pid);
      }
      if (pid) idMap[pid].push(productEls[i]);
    }
    if (ids.length === 0) return;
    window.BoostBundles.checkBatch(ids).then(function (results) {
      results.forEach(function (has, id) {
        if (!has || !idMap[id]) return;
        for (var j = 0; j < idMap[id].length; j++) {
          var imgDiv = idMap[id][j].querySelector('.isearch-product__image');
          if (imgDiv && !imgDiv.querySelector('.isearch-bundle-badge')) {
            var badge = document.createElement('button');
            badge.className = 'isearch-bundle-badge';
            badge.type = 'button';
            badge.textContent = 'Bundle';
            badge.setAttribute('data-bundle-pid', id);
            badge.addEventListener('click', function (pid) {
              return function (e) {
                e.preventDefault();
                e.stopPropagation();
                window.BoostBundles.openModal(pid);
              };
            }(id));
            imgDiv.appendChild(badge);
          }
        }
      });
    });
  };

  // ── Navigation ───────────────────────────────────

  InstantSearch.prototype._navigate = function (dir) {
    if (!this.items.length) return;

    if (this.activeIndex >= 0 && this.items[this.activeIndex]) {
      this.items[this.activeIndex].classList.remove('is-active');
    }

    this.activeIndex += dir;
    if (this.activeIndex >= this.items.length) this.activeIndex = 0;
    if (this.activeIndex < 0) this.activeIndex = this.items.length - 1;

    var el = this.items[this.activeIndex];
    if (el) {
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
    }
  };

  // ── Open / Close ─────────────────────────────────

  InstantSearch.prototype._open = function () {
    if (this.isOpen) return;
    this.isOpen = true;

    // On mobile (fixed positioning), place dropdown just below the input
    if (window.innerWidth <= 768) {
      var rect = this.input.getBoundingClientRect();
      this.dropdown.style.top = (rect.bottom + 6) + 'px';
    } else {
      this.dropdown.style.top = '';
    }

    // On mobile, adjust z-index so the search form sits above the overlay
    if (window.innerWidth <= 768) {
      var inputWrap = this.input.closest('.isearch-input-wrap');
      if (inputWrap) inputWrap.style.zIndex = '100';
      var topRow = document.querySelector('.site-header__row.site-header__row--top');
      if (topRow) topRow.style.zIndex = '0';
    }

    this._scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this._scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';

    this.dropdown.classList.add('is-open');
    this.overlay.classList.add('is-open');
    this.mobileOverlay.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
  };

  InstantSearch.prototype._close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo(0, this._scrollY || 0);

    this.dropdown.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.mobileOverlay.classList.remove('is-open');

    // Delay z-index reset until overlay fade-out completes
    var input = this.input;
    setTimeout(function () {
      var inputWrap = input.closest('.isearch-input-wrap');
      if (inputWrap) inputWrap.style.zIndex = '';
      var topRow = document.querySelector('.site-header__row.site-header__row--top');
      if (topRow) topRow.style.zIndex = '';
    }, 250);
    this.input.setAttribute('aria-expanded', 'false');
    this.activeIndex = -1;
    if (this.items.length) {
      for (var i = 0; i < this.items.length; i++) {
        this.items[i].classList.remove('is-active');
      }
    }
  };

  // ── Init ─────────────────────────────────────────

  function init() {
    // Desktop input
    var desktop = document.querySelector('#site-header-search .header-search__form__input');
    if (desktop) new InstantSearch(desktop);

    // Mobile input
    var mobile = document.querySelector('[data-search-form] .header-search__form__input');
    if (mobile) new InstantSearch(mobile);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
